import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import TableComponent from '../../../../components/table/TableComponent';
import { WarehouseModel } from '../../../../components/model/WarehouseModel';
import { useDispatch, useSelector } from 'react-redux';
import { RolesValidator } from '../../../../validations/RolesValidator';
import { ROLE } from '../../../../utils/ROLE';
import { useAlert } from 'react-alert';
import WarehouseForm from './WarehouseForm';
import { useOnMount } from '../../../../components/common/Hooks';
import { setDirection, setPage, setSortField } from '../../../../redux/slices/PaginationSlice';
import { deleteRecord, getWarehousePage, save, updateRecord, writeOffWarehouse } from './WarehouseService';
import { WarehouseFilter } from './WarehouseFilter';
import './Warehouse.css';
import WriteOffAllDayModal from './writeOffDay/WriteOffAllDayModal';
import { ConfirmationModal } from '../../../../components/modal/ConfirmationModal';
import { isMobile } from 'react-device-detect';
import { ToggleFilterButton } from '../../../../components/buttons/ToggleFilterButton';
import { EditButton } from '../../../../components/buttons/EditButton';
import { DeleteButton } from '../../../../components/buttons/DeleteButton';
import WriteOffModal from './WriteOffModal';
import { toDateString } from '../../../../utils/TimeUtils';
import { DEFAULT_DATE } from '../../../../components/common/Constants';

const WarehousePage = () => {

    const { t, i18n } = useTranslation();
    const alert = useAlert();
    const onMount = useOnMount();
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const pagination = useSelector((state) => state.pagination);
    const [records, setRecords] = useState([]);
    const [record, setRecord] = useState(WarehouseModel);
    const canHandleWarehouse = RolesValidator([ROLE.DIETIST, ROLE.MANAGER]);
    const [filterValue, setFilterValue] = useState({
        title: null,
        invoice: null,
        from: null,
        till: null,
    });
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const handleCloseModal = () => setIsConfirmOpen(false);

    useEffect(() => {
        if (onMount) {
            init();
        } else {
            loadData();
        }
    }, [pagination.page, pagination.sortByField, pagination.sortingDirection]);

    const init = () => {
        if (pagination.sortByField === 'productName'
            && pagination.page === 0
            && pagination.sortingDirection === 'ASC') {
            loadData();
        } else {
            dispatch(setSortField('productName'));
            dispatch(setPage(0));
            dispatch(setDirection('ASC'));
        }
    };

    const loadData = () => {
        let request = {
            page: pagination.page,
            size: pagination.size,
            ascDesc: pagination.sortingDirection,
            sortField: pagination.sortByField,
            lang: i18n.language.toUpperCase(),
            title: filterValue.title,
            invoice: filterValue.invoice,
            from: filterValue.from,
            till: filterValue.till,
        };
        setLoading(true);
        getWarehousePage(request)
            .then((response) => {
                setRecords(response.data);
            })
            .catch(() => alert.error(t('alert:loadingError')))
            .finally(() => setLoading(false));
    };

    const handleSubmit = (values, recordId) => {
        recordId !== -1
            ? update(values, recordId)
            : create(values);
    };

    const create = (record) => {
        save(record)
            .then(() => {
                alert.success(t('alert:saveSuccess'));
                loadData();
            })
            .catch(() => alert.error(t('alert:saveError')));
    };

    const update = (values, recordId) => {
        updateRecord({ ...values, id: recordId })
            .then(() => {
                alert.success(t('alert:saveSuccess'));
                loadData();
            })
            .catch(() => alert.error(t('alert:saveError')));
    };

    const openConfirmation = (warehouseModel) => {
        setItemToDelete(warehouseModel);
        setIsConfirmOpen(true);
    };

    const deleteWarehouse = () => {
        deleteRecord(itemToDelete.id)
            .then(() => {
                loadData();
                alert.success(t('alert:deleteSuccess'));
            })
            .catch(() => alert.error(t('alert:deleteError')));
        handleCloseModal();
    };

    const editRecord = (warehouseRecord) => {
        setRecord(warehouseRecord);
        document.getElementById('createButton').click();
    };

    const writeOffAction = (obj, amount, writeOffDate) => {
        writeOffWarehouse(obj.id, amount, toDateString(writeOffDate, DEFAULT_DATE))
            .then(() => {
                loadData();
                alert.success(t('alert:writeOffSuccess'));
            })
            .catch(() => alert.error(t('alert:writeOffError')));
    };

    const handleDayWriteOff = () => {
        loadData();
    };

    const handleCancel = () => {
        setRecord(WarehouseModel);
    };

    const getButtons = () => {
        return canHandleWarehouse
            ? [
                { element: <WriteOffModal onClick={writeOffAction} obj={'use obj'}/> },
                { element: <EditButton onClick={editRecord} /> },
                { element: <DeleteButton onClick={openConfirmation} /> },
            ]
            : [];
    };

    return (
        <>
            <div className={isMobile ? 'mobile-filter-container' : 'filter-container'}>
                {canHandleWarehouse ? (
                    <div>
                        <WarehouseForm onClick={handleSubmit}
                                       onCancel={handleCancel}
                                       record={record} />
                        <WriteOffAllDayModal onWriteOff={handleDayWriteOff}></WriteOffAllDayModal>
                        {isMobile ? <ToggleFilterButton></ToggleFilterButton> : <></>}
                    </div>
                ) : (
                    <></>
                )}
                <WarehouseFilter filter={filterValue} doFilter={loadData}></WarehouseFilter>
            </div>
            <ConfirmationModal
                message={t('warehouse:deleteConfirm') + (itemToDelete != null ? itemToDelete.productName : '') + '?'}
                isShowing={isConfirmOpen}
                onConfirm={deleteWarehouse}
                onClose={handleCloseModal}>
            </ConfirmationModal>
            {loading ? (
                <Spinner animation='border' className='container' />
            ) : (
                <TableComponent
                    model={WarehouseModel}
                    translationsGroup='warehouse:'
                    columnsNames={['productName', 'amount', 'invoice', 'invoiceDate', 'expiredDate']}
                    totalElements={records.totalElements}
                    contents={records.content}
                    buttons={getButtons()}
                />
            )}
        </>
    );
};

export default WarehousePage;
